import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyContextService } from '../../services/company-context/company-context.service';
import { CompanyInfo } from 'src/app/modules/auth/services/auth/response/authResponseDto.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

/**
 * Company Switcher Komponente für Multi-Company Users.
 *
 * Zeigt die aktuell aktive Company an und ermöglicht
 * den Wechsel zu anderen verfügbaren Companies.
 *
 * @example
 * <app-company-switcher
 *   [isCompact]="isCompact"
 *   (switched)="onCompanySwitched()"
 * ></app-company-switcher>
 */
@Component({
  selector: 'app-company-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './company-switcher.component.html',
  styleUrls: ['./company-switcher.component.css']
})
export class CompanySwitcherComponent implements OnInit, OnDestroy {
  /** Kompakter Modus (nur Icon) */
  @Input() isCompact: boolean = false;

  /** Mobile Modus */
  @Input() isMobileMode: boolean = false;

  /** Event wenn Company gewechselt wurde */
  @Output() switched = new EventEmitter<CompanyInfo>();

  /** Aktuell aktive Company */
  activeCompany: CompanyInfo | null = null;

  /** Verfügbare Companies */
  availableCompanies: CompanyInfo[] = [];

  /** Dropdown offen? */
  isDropdownOpen: boolean = false;

  /** Wird gerade gewechselt? */
  isLoading: boolean = false;

  /** Als Standard setzen Checkbox */
  setAsDefault: boolean = false;

  /** Hat mehrere Companies? */
  hasMultipleCompanies: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private companyContext: CompanyContextService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Aktive Company abonnieren
    this.companyContext.activeCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        this.activeCompany = company;
      });

    // Verfügbare Companies abonnieren
    this.companyContext.availableCompanies$
      .pipe(takeUntil(this.destroy$))
      .subscribe(companies => {
        this.availableCompanies = companies;
      });

    // Multi-Company Status abonnieren
    this.companyContext.hasMultipleCompanies$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasMultiple => {
        this.hasMultipleCompanies = hasMultiple;
      });

    // Loading Status abonnieren
    this.companyContext.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggle Dropdown
   */
  toggleDropdown(): void {
    if (this.isLoading) return;
    this.isDropdownOpen = !this.isDropdownOpen;

    // Reset checkbox when opening
    if (this.isDropdownOpen) {
      this.setAsDefault = false;
    }
  }

  /**
   * Dropdown schließen
   */
  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.setAsDefault = false;
  }

  /**
   * Company wechseln
   */
  switchToCompany(company: CompanyInfo): void {
    if (this.isLoading) return;

    // Gleiche Company? Nur schließen
    if (company.CompanyId === this.activeCompany?.CompanyId && !this.setAsDefault) {
      this.closeDropdown();
      return;
    }

    this.companyContext.switchCompany(company.CompanyId, this.setAsDefault)
      .subscribe(success => {
        if (success) {
          this.toastr.success(
            this.translate.instant('COMPANY_SWITCH_SUCCESS', { companyName: company.CompanyName }),
            this.translate.instant('SUCCESS')
          );
          this.closeDropdown();
          this.switched.emit(company);

          // Seite neu laden um alle Daten zu aktualisieren
          window.location.reload();
        } else {
          this.toastr.error(
            this.translate.instant('COMPANY_SWITCH_FAILED'),
            this.translate.instant('ERROR')
          );
        }
      });
  }

  /**
   * Gibt das Rollen-Icon zurück
   */
  getRoleIcon(role: number): string {
    switch (role) {
      case 1: return 'fa-user-tie';
      case 2: return 'fa-user';
      case 3: return 'fa-users-gear';
      default: return 'fa-user';
    }
  }

  /**
   * Gibt die Rollen-Bezeichnung zurück
   */
  getRoleLabel(role: number): string {
    switch (role) {
      case 1: return 'Manager';
      case 2: return this.translate.instant('EMPLOYEE');
      case 3: return this.translate.instant('TEAM_LEADER');
      default: return this.translate.instant('EMPLOYEE');
    }
  }

  /**
   * Prüft ob Company die aktive ist
   */
  isActiveCompany(company: CompanyInfo): boolean {
    return company.CompanyId === this.activeCompany?.CompanyId;
  }

  /**
   * Verhindert Bubbling beim Klick
   */
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
